import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import FieldsDiffTable from "@entrilia/shared/components/FieldsDiffTable";
import type { ChangeRequestDetails } from "../../../../api/types/changeRequestsTypes";
import PendingChangeRequestDialog from "../common/PendingChangeRequestDialog";
import PendingChangesSection from "../common/PendingChangesSection";
import useBankAccountsContext from "./useBankAccountsContext";

interface Props {
  onChangeRequestCanceled: (workflowId: string) => void;
}

const ChangeRequestsStatus = ({ onChangeRequestCanceled }: Props) => {
  const { canUserEdit, investorId, pendingChangeRequests } = useBankAccountsContext();

  const [showPendingChangeRequestDialog, setShowPendingChangeRequestDialog] = useState(false);
  const [visiblePendingChangeRequest, setVisiblePendingChangeRequest] = useState<ChangeRequestDetails>();

  if (pendingChangeRequests.length === 0) {
    return null;
  }

  const handleViewRequest = (changeRequest: ChangeRequestDetails) => {
    setVisiblePendingChangeRequest(changeRequest);
    setShowPendingChangeRequestDialog(true);
  };

  const handleChangeRequestCanceled = () => {
    if (visiblePendingChangeRequest) {
      onChangeRequestCanceled(visiblePendingChangeRequest.workflowId);
    }
    setShowPendingChangeRequestDialog(false);
  };

  return (
    <>
      <PendingChangesSection
        canUserEdit={canUserEdit}
        pendingChangeRequests={pendingChangeRequests}
        onViewRequest={handleViewRequest}
      />
      {visiblePendingChangeRequest && (
        <PendingChangeRequestDialog
          open={showPendingChangeRequestDialog}
          onClose={() => setShowPendingChangeRequestDialog(false)}
          canUserEdit={canUserEdit}
          investorId={investorId}
          changeRequestDetails={visiblePendingChangeRequest}
          onChangeRequestCanceled={handleChangeRequestCanceled}
        >
          {visiblePendingChangeRequest.fieldChanges?.length && (
            <>
              <Typography variant="subtitle1">Bank Details</Typography>
              <FieldsDiffTable fieldChanges={visiblePendingChangeRequest.fieldChanges} newValueColumnLabel="New" />
            </>
          )}
          {visiblePendingChangeRequest.allowedAssignedEntityChanges?.length && (
            <>
              <Typography variant="subtitle1">Fund Assignments</Typography>
              <FieldsDiffTable
                fieldChanges={visiblePendingChangeRequest.allowedAssignedEntityChanges.map((change) => ({
                  ...change,
                  key: change.displayName,
                  fieldType: "Text",
                }))}
                oldValueColumnLabel="Current"
                newValueColumnLabel="Change"
                sortKeys
              />
            </>
          )}
          {visiblePendingChangeRequest.comment && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">Comment</Typography>
              <Typography color="textSecondary" sx={{ whiteSpace: "break-spaces" }}>
                {visiblePendingChangeRequest.comment}
              </Typography>
            </Stack>
          )}
        </PendingChangeRequestDialog>
      )}
    </>
  );
};

export default ChangeRequestsStatus;
